/**
 * The viewer's default candle style — hollow for a member of the JAS group,
 * standard for everyone else (owner, 2026-10-05). `PlotPalette.candleStyle`
 * absent means *this*, and an explicit pick on a plot always wins.
 *
 * **Resolved here and never written back.** The default is applied only on the
 * way to the renderer (`withCandleDefault`), so a JAS member who never touches
 * the setting stores nothing, and leaving the group changes their charts —
 * where writing it into every price plot would freeze today's membership into
 * the workspace.
 *
 * Module scope with subscribers, `symbol-index.ts`'s shape: one fact about the
 * signed-in person, read by every chart on the page.
 */

import { useSyncExternalStore } from "react";
import type { Pane } from "@qufolio/model";

export type CandleStyle = "standard" | "hollow";

/**
 * The group whose members default to hollow, under its key before and after
 * the rename to `st-jas` (`docs/plans/st-group-rename.md`), so the default
 * holds on either side of the migration.
 */
export const HOLLOW_GROUPS: readonly string[] = ["st-jas", "grp-jas"];

let current: CandleStyle = "standard";
const listeners = new Set<() => void>();

/** Set from the signed-in person's groups (`/auth/me`'s `orgs`). */
export function setCandleDefaultFromGroups(memberOf: readonly string[]): void {
  const next: CandleStyle = HOLLOW_GROUPS.some((key) => memberOf.includes(key)) ? "hollow" : "standard";
  if (next === current) return;
  current = next;
  for (const listener of listeners) listener();
}

export function defaultCandleStyle(): CandleStyle {
  return current;
}

export function useDefaultCandleStyle(): CandleStyle {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}

/**
 * The panes as the renderer should draw them: every candlestick that has not
 * picked a style takes `fallback`. Returns `panes` by identity when nothing
 * changes, so a standard viewer pays nothing.
 */
export function withCandleDefault(panes: readonly Pane[], fallback: CandleStyle): Pane[] {
  if (fallback === "standard") return panes as Pane[];
  let changed = false;
  const out = panes.map((pane) => {
    if (!pane.plots?.some((plot) => plot.type === "candlestick" && !plot.palette?.candleStyle)) {
      return pane;
    }
    changed = true;
    return {
      ...pane,
      plots: pane.plots.map((plot) =>
        plot.type === "candlestick" && !plot.palette?.candleStyle
          ? { ...plot, palette: { ...plot.palette, candleStyle: fallback } }
          : plot,
      ),
    };
  });
  return changed ? out : (panes as Pane[]);
}
